import { useEffect, useState } from "react";

import { Logger } from "@liberty-fund/logger";
import { getAccessToken } from "@liberty-fund/utils/auth";

interface AssetImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /**
   * Asset URL to render. The GraphQL API returns a conventional asset URL for
   * every record, even those with no underlying asset, so a truthy `src` does
   * not guarantee the asset exists — the request may 404 (missing) or 403
   * (permissions). Renders `fallback` when `src` is empty/null or the fetch
   * fails.
   */
  src?: string | null;
  /** Rendered when there is no `src` or the image fails to load. */
  fallback: React.ReactNode;
}

/**
 * Renders an authenticated asset.
 *
 * The assets service requires a bearer token, and a browser cannot attach one
 * to `<img src>`. Under the Node container this was solved by proxying through
 * `/api/util/fetch-asset`, which stamped the header server-side. A static SPA
 * has no such proxy, so the image is fetched with `fetch()` and handed to the
 * `<img>` as an object URL instead.
 *
 * This requires the assets domain to send `Access-Control-Allow-Origin` for the
 * portal origin — see docs/CLOUD_API_REQUIREMENTS.md.
 */
const AssetImage = ({
  src,
  fallback,
  alt = "",
  onError,
  ...rest
}: AssetImageProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  // Track the failed URL rather than a boolean so the state self-resets when
  // `src` changes without the component remounting (e.g. the same <AssetImage>
  // reused across person → person navigation).
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!src) {
      setObjectUrl(null);
      return;
    }

    let cancelled = false;
    let created: string | null = null;

    (async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await fetch(src, {
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : undefined,
        });

        if (!response.ok) {
          throw new Error(`Asset request failed with ${response.status}`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      } catch (error) {
        Logger.debug("AssetImage failed to load asset", {
          src,
          message: (error as Error)?.message,
        });
        if (!cancelled) setFailedSrc(src);
      }
    })();

    return () => {
      cancelled = true;
      // Revoking on cleanup rather than on load keeps the blob alive for as
      // long as the <img> references it, and prevents a leak per navigation.
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);

  if (!src || failedSrc === src || !objectUrl) {
    return <>{fallback}</>;
  }

  return (
    <img
      {...rest}
      src={objectUrl}
      alt={alt}
      onError={(event) => {
        setFailedSrc(src);
        onError?.(event);
      }}
    />
  );
};

AssetImage.displayName = "AssetImage";

export default AssetImage;
export type { AssetImageProps };
